import { Routes, Route } from 'react-router-dom';
import { WalletProvider } from './context/WalletContext';
import Layout from './components/Layout';
import HomePage from './pages/HomePage';
import NotFoundPage from './pages/NotFoundPage';

// Legacy pages (will be replaced/deprecated)
import AuctionsPage from './pages/AuctionsPage';
import AuctionDetailPage from './pages/AuctionDetailPage';
import CollectionsPage from './pages/CollectionsPage';
import CollectionDetailPage from './pages/CollectionDetailPage';
import StakingPage from './pages/StakingPage';
import GovernancePage from './pages/GovernancePage';
import ProposalDetailPage from './pages/ProposalDetailPage';
import ProfilePage from './pages/ProfilePage';
import LeaderboardPage from './pages/LeaderboardPage';
import TokenomicsPage from './pages/TokenomicsPage';
import FractionalPage from './pages/FractionalPage';

// New Public Pages
import ExplorePage from './pages/public/ExplorePage';
import LiveAuctionsPage from './pages/public/LiveAuctionsPage';
import HowItWorksPage from './pages/public/HowItWorksPage';
import MarketPricingPage from './pages/public/MarketPricingPage';

// Dashboard Components & Pages
import DashboardLayout from './components/DashboardLayout';
import ProtectedRoute from './components/ProtectedRoute';
import DashboardHome from './pages/dashboard/DashboardHome';
import WatchlistPage from './pages/dashboard/WatchlistPage';
import BidsPage from './pages/dashboard/BidsPage';
import PurchasesPage from './pages/dashboard/PurchasesPage';
import DashboardStakingPage from './pages/dashboard/StakingPage';
import DashboardGovernancePage from './pages/dashboard/GovernancePage';
import CreateAuctionPage from './pages/dashboard/CreateAuctionPage';
import DashboardProfilePage from './pages/dashboard/ProfilePage';
import InvoicesPage from './pages/dashboard/InvoicesPage';
import InvoiceDetailPage from './pages/dashboard/InvoiceDetailPage';

// Legal Pages
import TermsOfServicePage from './pages/legal/TermsOfServicePage';
import PrivacyPolicyPage from './pages/legal/PrivacyPolicyPage';
import CookiePolicyPage from './pages/legal/CookiePolicyPage';

// Company Pages
import AboutPage from './pages/company/AboutPage';
import CareersPage from './pages/company/CareersPage';
import ContactPage from './pages/company/ContactPage';
import PressKitPage from './pages/company/PressKitPage';
import BlogPage from './pages/company/BlogPage';

// Resources Pages
import WhitepaperPage from './pages/resources/WhitepaperPage';
import DocumentationPage from './pages/resources/DocumentationPage';
import APIReferencePage from './pages/resources/APIReferencePage';
import SmartContractsPage from './pages/resources/SmartContractsPage';
import BrandAssetsPage from './pages/resources/BrandAssetsPage';

// Platform Pages
import EcosystemPage from './pages/platform/EcosystemPage';
import NDHTokenPage from './pages/platform/NDHTokenPage';
import StakingInfoPage from './pages/platform/StakingInfoPage';
import GovernanceInfoPage from './pages/platform/GovernanceInfoPage';
import CurationPage from './pages/platform/CurationPage';
import LiquidityMiningPage from './pages/platform/LiquidityMiningPage';

function App() {
  return (
    <WalletProvider>
      <Routes>
        {/* Main Layout Routes */}
        <Route element={<Layout />}>
          {/* Home */}
          <Route path="/" element={<HomePage />} />

          {/* New Public Pages (Header Navigation) */}
          <Route path="/explore" element={<ExplorePage />} />
          <Route path="/live" element={<LiveAuctionsPage />} />
          <Route path="/how-it-works" element={<HowItWorksPage />} />
          <Route path="/pricing" element={<MarketPricingPage />} />

          {/* Legacy Auctions Routes (redirect to explore/live) */}
          <Route path="/auctions" element={<AuctionsPage />} />
          <Route path="/auctions/:id" element={<AuctionDetailPage />} />
          <Route path="/auction/:id" element={<AuctionDetailPage />} />

          {/* Collections */}
          <Route path="/collections" element={<CollectionsPage />} />
          <Route path="/collections/:id" element={<CollectionDetailPage />} />

          {/* Legacy Routes (still accessible for backwards compatibility) */}
          <Route path="/staking" element={<StakingPage />} />
          <Route path="/tokenomics" element={<TokenomicsPage />} />
          <Route path="/governance" element={<GovernancePage />} />
          <Route path="/governance/:id" element={<ProposalDetailPage />} />
          <Route path="/leaderboard" element={<LeaderboardPage />} />
          <Route path="/fractional" element={<FractionalPage />} />
          <Route path="/profile/:address" element={<ProfilePage />} />

          {/* Legal Pages */}
          <Route path="/legal/terms" element={<TermsOfServicePage />} />
          <Route path="/legal/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/legal/cookies" element={<CookiePolicyPage />} />

          {/* Company Pages */}
          <Route path="/about" element={<AboutPage />} />
          <Route path="/careers" element={<CareersPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/press-kit" element={<PressKitPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogPage />} />

          {/* Resources Pages */}
          <Route path="/whitepaper" element={<WhitepaperPage />} />
          <Route path="/docs" element={<DocumentationPage />} />
          <Route path="/docs/api" element={<APIReferencePage />} />
          <Route path="/docs/contracts" element={<SmartContractsPage />} />
          <Route path="/brand-assets" element={<BrandAssetsPage />} />

          {/* Platform Pages */}
          <Route path="/ecosystem" element={<EcosystemPage />} />
          <Route path="/ndh-token" element={<NDHTokenPage />} />
          <Route path="/staking-info" element={<StakingInfoPage />} />
          <Route path="/governance-info" element={<GovernanceInfoPage />} />
          <Route path="/curation" element={<CurationPage />} />
          <Route path="/liquidity-mining" element={<LiquidityMiningPage />} />

          {/* 404 */}
          <Route path="*" element={<NotFoundPage />} />
        </Route>

        {/* Dashboard Routes (Protected - Require Wallet Connection) */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<DashboardHome />} />
          <Route path="watchlist" element={<WatchlistPage />} />
          <Route path="bids" element={<BidsPage />} />
          <Route path="purchases" element={<PurchasesPage />} />
          <Route path="staking" element={<DashboardStakingPage />} />
          <Route path="governance" element={<DashboardGovernancePage />} />
          <Route path="create" element={<CreateAuctionPage />} />
          <Route path="profile" element={<DashboardProfilePage />} />
          <Route path="invoices" element={<InvoicesPage />} />
          <Route path="invoices/:invoiceId" element={<InvoiceDetailPage />} />
          <Route path="notifications" element={<DashboardHome />} /> {/* TODO: Create NotificationsPage */}
        </Route>
      </Routes>
    </WalletProvider>
  );
}

export default App;
